import Animated, {
  Easing,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import React, { useEffect, useState } from 'react';
import {
  TextInput as BaseTextInput,
  StyleProp,
  Text,
  TextInputProps,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';

export const TEXT_INPUT_HEIGHT = 40;
export const TEXTAREA_MIN_HEIGHT = 80;

type Props = TextInputProps & {
  containerStyle?: StyleProp<ViewStyle>;
  error?: string | null;
  focusedBorderColor?: string;
};

/**
 * Sales Portal-scoped TextInput wrapper.
 *
 * Note: We intentionally do NOT sanitize while typing (to avoid rendering entities like `&lt;`
 * as the user types). Sanitization is done at submit-time in the checkout flow.
 */
export const TextInput: React.FC<Props> = ({
  containerStyle,
  error,
  focusedBorderColor,
  onBlur,
  onFocus,
  placeholderTextColor,
  style,
  ...rest
}) => {
  const [styles, theme] = useStyles();
  const [isFocused, setIsFocused] = useState(false);
  const isMultiline = !!rest.multiline;
  const focusAnimatedValue = useSharedValue(0);

  const focusBorderColor =
    focusedBorderColor ?? theme.sk.color.border.input.active;

  useEffect(() => {
    focusAnimatedValue.value = withTiming(isFocused ? 1 : 0, {
      duration: 250,
      easing: Easing.linear,
    });
  }, [focusAnimatedValue, isFocused]);

  const animatedContainerStyle = useAnimatedStyle(
    () => ({
      borderColor: interpolateColor(
        focusAnimatedValue.value,
        [0, 1],
        error
          ? [theme.sk.color.text.error, theme.sk.color.text.error]
          : [theme.sk.color.border.input.default, focusBorderColor],
      ),
    }),
    [error, focusBorderColor, theme],
  );

  return (
    <>
      <Animated.View
        style={[
          styles.container,
          isMultiline ? styles.containerMultiline : null,
          containerStyle,
          animatedContainerStyle,
        ]}
      >
        <BaseTextInput
          onBlur={(event) => {
            setIsFocused(false);
            onBlur?.(event);
          }}
          onFocus={(event) => {
            setIsFocused(true);
            onFocus?.(event);
          }}
          placeholderTextColor={
            placeholderTextColor ?? theme.sk.color.text.hint
          }
          selectionColor={theme.sk.color.border.input.active}
          style={[
            styles.input,
            isMultiline ? styles.inputMultiline : styles.inputSingleLine,
            style,
          ]}
          {...rest}
        />
      </Animated.View>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </>
  );
};

const useStyles = createStyles((theme) => {
  const { lineHeight, ...paragraphBase } = theme.sk.typography.paragraph.base;

  return {
    container: {
      backgroundColor: theme.sk.color.bg.primary.default,
      borderColor: theme.sk.color.border.input.default,
      borderRadius: theme.sk.radii.radiusMedium,
      borderWidth: 1,
      height: TEXT_INPUT_HEIGHT,
      justifyContent: 'center',
    },
    containerMultiline: {
      alignItems: 'flex-start',
      height: 'auto',
      minHeight: TEXTAREA_MIN_HEIGHT,
      paddingVertical: theme.sk.spacing.spaceSMedium,
    },
    errorText: {
      ...theme.sk.typography.paragraph.sm,
      color: theme.sk.color.text.error,
      fontWeight: 400,
      marginTop: theme.sk.spacing.spaceXsmall,
    },
    input: {
      ...paragraphBase,
      color: theme.sk.color.text.primary,
      flex: 1,
      fontWeight: 400,
      paddingHorizontal: theme.sk.spacing.spaceMedium,
      paddingVertical: 0,
      width: '100%',
    },
    inputMultiline: {
      lineHeight,
      textAlignVertical: 'top',
    },
    inputSingleLine: {
      textAlignVertical: 'center',
    },
  };
});
